import { Save, RotateCcw, Zap, Database, Timer, HardDrive } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { TabsContent } from '@/components/ui/tabs';

const SystemSettingsPerformance = ({
  form,
  onSubmit,
  saving,
  resetToDefaults,
}) => {
  const formatDuration = seconds => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  return (
    <TabsContent value='performance'>
      <Card>
        <CardHeader>
          <CardTitle>Performance Settings</CardTitle>
          <CardDescription>
            Configure system performance parameters, rate limits, and resource
            allocation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
              <div className='space-y-4'>
                <h3 className='text-lg font-medium flex items-center'>
                  <Zap className='h-5 w-5 mr-2' />
                  API Performance
                </h3>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <FormField
                    control={form.control}
                    name='apiRateLimit'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>API Rate Limit (requests/minute)</FormLabel>
                        <FormControl>
                          <div className='space-y-3'>
                            <Slider
                              min={10}
                              max={10000}
                              step={10}
                              value={[field.value]}
                              onValueChange={value => field.onChange(value[0])}
                              className='w-full'
                            />
                            <div className='text-center text-sm text-muted-foreground'>
                              {field.value.toLocaleString()} requests per minute
                            </div>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Maximum API requests allowed per minute per user.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='maxConcurrentUploads'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Concurrent Uploads</FormLabel>
                        <FormControl>
                          <div className='space-y-3'>
                            <Slider
                              min={1}
                              max={20}
                              step={1}
                              value={[field.value]}
                              onValueChange={value => field.onChange(value[0])}
                              className='w-full'
                            />
                            <div className='text-center text-sm text-muted-foreground'>
                              {field.value} concurrent uploads
                            </div>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Maximum number of simultaneous file uploads per user.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              <div className='space-y-4'>
                <h3 className='text-lg font-medium flex items-center'>
                  <Timer className='h-5 w-5 mr-2' />
                  Processing Timeouts
                </h3>

                <FormField
                  control={form.control}
                  name='imageProcessingTimeout'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Image Processing Timeout</FormLabel>
                      <FormControl>
                        <div className='space-y-3'>
                          <Slider
                            min={30}
                            max={600}
                            step={30}
                            value={[field.value]}
                            onValueChange={value => field.onChange(value[0])}
                            className='w-full'
                          />
                          <div className='text-center text-sm text-muted-foreground'>
                            {formatDuration(field.value)} timeout
                          </div>
                        </div>
                      </FormControl>
                      <FormDescription>
                        Maximum time allowed for image processing operations.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Separator />

              <div className='space-y-4'>
                <h3 className='text-lg font-medium flex items-center'>
                  <Database className='h-5 w-5 mr-2' />
                  Database Settings
                </h3>

                <FormField
                  control={form.control}
                  name='databaseConnectionPoolSize'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Database Connection Pool Size</FormLabel>
                      <FormControl>
                        <div className='space-y-3'>
                          <Slider
                            min={5}
                            max={100}
                            step={5}
                            value={[field.value]}
                            onValueChange={value => field.onChange(value[0])}
                            className='w-full'
                          />
                          <div className='text-center text-sm text-muted-foreground'>
                            {field.value} connections in pool
                          </div>
                        </div>
                      </FormControl>
                      <FormDescription>
                        Number of database connections to maintain in the pool.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Separator />

              <div className='space-y-4'>
                <h3 className='text-lg font-medium flex items-center'>
                  <HardDrive className='h-5 w-5 mr-2' />
                  Caching & Optimization
                </h3>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <FormField
                    control={form.control}
                    name='cacheTtl'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cache TTL (Time to Live)</FormLabel>
                        <FormControl>
                          <div className='space-y-3'>
                            <Slider
                              min={60}
                              max={86400}
                              step={60}
                              value={[field.value]}
                              onValueChange={value => field.onChange(value[0])}
                              className='w-full'
                            />
                            <div className='text-center text-sm text-muted-foreground'>
                              {formatDuration(field.value)} cache lifetime
                            </div>
                          </div>
                        </FormControl>
                        <FormDescription>
                          How long cached data remains valid.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <FormField
                    control={form.control}
                    name='cacheEnabled'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                        <div className='space-y-0.5'>
                          <FormLabel className='text-base'>
                            Enable Caching
                          </FormLabel>
                          <FormDescription>
                            Enable in-memory caching for improved performance.
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='compressionEnabled'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                        <div className='space-y-0.5'>
                          <FormLabel className='text-base'>
                            Enable Compression
                          </FormLabel>
                          <FormDescription>
                            Enable gzip compression for API responses.
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              <div className='space-y-4'>
                <h3 className='text-lg font-medium'>Performance Monitoring</h3>

                <div className='bg-muted/50 p-4 rounded-lg'>
                  <p className='text-sm text-muted-foreground mb-4'>
                    Current system performance metrics and recommendations.
                  </p>

                  <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                    <div className='space-y-2'>
                      <div className='flex items-center justify-between'>
                        <span className='text-sm font-medium'>CPU Usage</span>
                        <Badge variant='secondary'>Normal</Badge>
                      </div>
                      <div className='w-full bg-background rounded-full h-2'>
                        <div
                          className='bg-green-500 h-2 rounded-full'
                          style={{ width: '45%' }}
                        ></div>
                      </div>
                      <p className='text-xs text-muted-foreground'>
                        45% average
                      </p>
                    </div>

                    <div className='space-y-2'>
                      <div className='flex items-center justify-between'>
                        <span className='text-sm font-medium'>
                          Memory Usage
                        </span>
                        <Badge variant='secondary'>Normal</Badge>
                      </div>
                      <div className='w-full bg-background rounded-full h-2'>
                        <div
                          className='bg-blue-500 h-2 rounded-full'
                          style={{ width: '62%' }}
                        ></div>
                      </div>
                      <p className='text-xs text-muted-foreground'>
                        62% average
                      </p>
                    </div>

                    <div className='space-y-2'>
                      <div className='flex items-center justify-between'>
                        <span className='text-sm font-medium'>
                          Response Time
                        </span>
                        <Badge variant='secondary'>Good</Badge>
                      </div>
                      <div className='w-full bg-background rounded-full h-2'>
                        <div
                          className='bg-green-500 h-2 rounded-full'
                          style={{ width: '25%' }}
                        ></div>
                      </div>
                      <p className='text-xs text-muted-foreground'>
                        125ms average
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              <div className='space-y-4'>
                <h3 className='text-lg font-medium'>
                  Optimization Recommendations
                </h3>

                <div className='space-y-3'>
                  <div className='flex items-start space-x-3 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800'>
                    <div className='w-2 h-2 bg-green-500 rounded-full mt-2'></div>
                    <div>
                      <p className='text-sm font-medium text-green-800 dark:text-green-200'>
                        Cache Hit Rate: 89%
                      </p>
                      <p className='text-xs text-green-600 dark:text-green-400'>
                        Excellent cache performance. Consider increasing cache
                        TTL for better efficiency.
                      </p>
                    </div>
                  </div>

                  <div className='flex items-start space-x-3 p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800'>
                    <div className='w-2 h-2 bg-yellow-500 rounded-full mt-2'></div>
                    <div>
                      <p className='text-sm font-medium text-yellow-800 dark:text-yellow-200'>
                        Database Pool Utilization: 78%
                      </p>
                      <p className='text-xs text-yellow-600 dark:text-yellow-400'>
                        Consider increasing pool size during peak hours for
                        better performance.
                      </p>
                    </div>
                  </div>

                  <div className='flex items-start space-x-3 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800'>
                    <div className='w-2 h-2 bg-blue-500 rounded-full mt-2'></div>
                    <div>
                      <p className='text-sm font-medium text-blue-800 dark:text-blue-200'>
                        API Rate Limit Usage: 45%
                      </p>
                      <p className='text-xs text-blue-600 dark:text-blue-400'>
                        Current rate limits are appropriate for the current
                        load.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className='flex justify-between'>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => resetToDefaults(form, 'performance')}
                >
                  <RotateCcw className='h-4 w-4 mr-2' />
                  Reset to Defaults
                </Button>
                <Button type='submit' disabled={saving}>
                  <Save className='h-4 w-4 mr-2' />
                  {saving ? 'Saving...' : 'Save Performance Settings'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </TabsContent>
  );
};

export default SystemSettingsPerformance;
